import * as React from 'react'

import { useLazyQuery } from '@apollo/client'

import {
  CanvaIcon,
  DropboxIcon,
  GoogleDriveIcon,
  GooglePhotosIcon,
  OneDriveIcon,
  UnsplashIcon,
} from '@buffer-mono/popcorn'
import { type Uploader, UploadSource } from '@buffer-mono/uploader'

import type { Service } from '~publish/gql/graphql'
import {
  getCanvaDesignConfig,
  openCanvaModal,
} from '~publish/legacy/canva/handlers'
import { useIsIntegrationUploading } from '~publish/legacy/integrations-bar/state/useIsIntegrationUploading'
import { addPermanentNotification } from '~publish/legacy/notifications-provider/notificationsSlice'
import { useAppDispatch } from '~publish/legacy/store'
import { logError } from '~publish/legacy/utils/logError'

import {
  CANVA_SERVICE,
  DROPBOX_SERVICE,
  GOOGLE_DRIVE_SERVICE,
  GOOGLE_PHOTOS_SERVICE,
  ONE_DRIVE_SERVICE,
  UNSPLASH_SERVICE,
} from './constants'
import { TRACK_IMAGES_QUERY } from './queries'
import {
  BUTTON_CLICKED,
  MEDIA_ATTACHED,
  sendIntegrationsEvent,
} from './tracking'
import type {
  GraphImage,
  IntegrationsContext,
  IntegrationsService,
} from './types'
import {
  UploadcareIntegrations,
  UploadcareSources,
} from '../uploadcare/constants'
import { openUploadcareModal } from '../uploadcare/openUploadcareModal'

/*
 * HOOK: useIntegrationServices
 */
export const useIntegrationServices = ({
  context,
  uploader,
  updateType,
  serviceName = 'omni',
  onError,
  enableCanva = true,
  enableUnsplash = true,
}: {
  context: IntegrationsContext
  uploader: Uploader
  updateType?: string
  serviceName?: Service | 'omni' | 'instagramStory'
  onError?: ({ message }: { message: string }) => void
  enableCanva?: boolean
  enableUnsplash?: boolean
}): {
  services: Array<IntegrationsService>
  activeService: IntegrationsService | null
} => {
  const dispatch = useAppDispatch()
  const [activeService, setActiveService] =
    React.useState<IntegrationsService | null>(null)

  const serviceClose = (): void => {
    setActiveService(null)
  }

  const serviceOpen = (service: IntegrationsService): void => {
    service.onClose = serviceClose
    setActiveService(service)
    sendIntegrationsEvent(BUTTON_CLICKED, context, service.id)

    service.onClick?.()
  }

  React.useEffect(() => {
    if (activeService) {
      // Pushing the change to the end of the call stack
      const timer = setTimeout(() => {
        document.body.style.pointerEvents = ''
      }, 0)

      return () => clearTimeout(timer)
    } else {
      document.body.style.pointerEvents = 'auto'
    }
  }, [activeService])

  const { onUploadStarted, onUploadFinished } = useIsIntegrationUploading()

  const [trackImages] = useLazyQuery(TRACK_IMAGES_QUERY)

  if (!onError) {
    onError = ({ message }: { message: string }): void => {
      dispatch(addPermanentNotification(`⚠️ ${message}`))
    }
  }

  /*
   * uploadMediaFile
   *
   * Fetches the file from url, calls uploader.addFilesFromUrls,
   * sends MEDIA_ATTACHED event for integrations and set activeService=null
   */
  const uploadMediaFile = async (
    url: string,
    source: UploadSource,
    name?: string,
    downloadUrl?: string,
  ): Promise<void> => {
    const remoteFile = {
      remoteUrl: url,
      name,
    }
    try {
      onUploadStarted()
      serviceClose()

      const files = await uploader.addFilesFromUrls(remoteFile, source)

      onUploadFinished()
      sendIntegrationsEvent(
        MEDIA_ATTACHED,
        context,
        source.name,
        files.successful[0]?.type,
      )

      if (source.name === UNSPLASH_SERVICE) {
        trackImages({
          variables: {
            trackUrl: downloadUrl,
          },
        })
      }
    } catch (error) {
      // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      logError(error, { metaData: { integration: source.name, remoteFile } })
      // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
      onError?.({ message: `Failed to upload media ${error.message}` })
    }
  }

  const openUploadCare = (integration: string): void => {
    openUploadcareModal(
      serviceName === 'instagramStory' ? 'instagram' : serviceName,
      uploader,
      onError,
      integration,
      onUploadStarted,
      onUploadFinished,
      UploadcareSources.BAR,
      undefined,
      updateType,
      context,
    )
  }

  // INCLUDES HERE THE SERVICES FOR THE INTEGRATIONS BAR
  const services: IntegrationsService[] = [
    {
      id: CANVA_SERVICE,
      title: 'Canva',
      label: 'Design with Canva',
      placeholder: '', // not required for canva
      isNew: false,
      isEnabled: enableCanva,
      icon: <CanvaIcon />,
      openSearchImagesModal: false,
      onClick: (): void => {
        openCanvaModal({
          uploader,
          designConfig: getCanvaDesignConfig(serviceName),
          onClose: serviceClose,
        })
      },
    },
    {
      id: DROPBOX_SERVICE,
      title: 'Dropbox',
      label: 'Select from Dropbox',
      isNew: false,
      isEnabled: true,
      icon: <DropboxIcon />,
      openSearchImagesModal: false,
      onClick: (): void => {
        openUploadCare(UploadcareIntegrations.DROPBOX)
      },
    },
    {
      id: GOOGLE_DRIVE_SERVICE,
      title: 'Google Drive',
      label: 'Select from Google Drive',
      isNew: false,
      isEnabled: true,
      icon: <GoogleDriveIcon />,
      openSearchImagesModal: false,
      onClick: (): void => {
        openUploadCare(UploadcareIntegrations.GOOGLE_DRIVE)
      },
    },
    {
      id: GOOGLE_PHOTOS_SERVICE,
      title: 'Google Photos',
      label: 'Select from Google Photos',
      isNew: false,
      isEnabled: true,
      icon: <GooglePhotosIcon />,
      openSearchImagesModal: false,
      onClick: (): void => {
        openUploadCare(UploadcareIntegrations.GOOGLE_PHOTOS)
      },
    },

    {
      id: ONE_DRIVE_SERVICE,
      title: 'One Drive',
      label: 'Select from OneDrive',
      isNew: false,
      isEnabled: true,
      icon: <OneDriveIcon />,
      openSearchImagesModal: false,
      onClick: (): void => {
        openUploadCare(UploadcareIntegrations.ONE_DRIVE)
      },
    },
    {
      id: UNSPLASH_SERVICE,
      title: 'Unsplash',
      label: 'Select from Unsplash',
      placeholder: 'Search free high resolution photos',
      isNew: false,
      isEnabled: enableUnsplash,
      icon: <UnsplashIcon />,
      openSearchImagesModal: true,
      onFileSelected: async (imageTarget: GraphImage): Promise<void> => {
        uploadMediaFile(
          imageTarget.regularUrl,
          UploadSource.unsplash({
            id: imageTarget.id,
            author: imageTarget.author,
          }),
          `${imageTarget.id}-unsplash.jpg`,
          imageTarget.downloadUrl,
        )
      },
    },
  ]

  return {
    services: services
      .filter((s) => s.isEnabled)
      .map((s) => {
        return {
          ...s,
          onClick: () => serviceOpen(s),
        }
      }),
    activeService,
  }
}
